import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

// eslint-disable-next-line max-lines-per-function
const AdvancedNetworkViz = ({ width = 900, height = 700, selectedMetric = 'trust', filteredRelationships = null, onNodeClick = null }) => {
  const svgRef = useRef();
  const [selectedNode, setSelectedNode] = useState(null);
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });

  // Enhanced Force-Directed Graph with Better Stability
  // eslint-disable-next-line max-lines-per-function
  const renderStableForceGraph = (svg, data) => {
    svg.selectAll('*').remove();

    // Create a stable layout with controlled forces
    const simulation = d3.forceSimulation(data.nodes)
      .force('link', d3.forceLink(data.links)
        .id(d => d.id)
        .distance(d => 100 - (d.strength * 8)) // Stronger relationships = closer nodes
        .strength(0.3))
      .force('charge', d3.forceManyBody()
        .strength(d => -300 - (d.totalConnections * 20))) // More connected = more repulsion
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(d => 25 + d.totalConnections * 2))
      .alphaDecay(0.02) // Slower cooling for stability
      .velocityDecay(0.8); // Higher damping for less jitter

    // Create link elements
    const links = svg.append('g')
      .selectAll('line')
      .data(data.links)
      .enter()
      .append('line')
      .style('stroke', d => {
        const colorScale = d3.scaleSequential(d3.interpolateRdYlGn).domain([1, 10]);
        return colorScale(d[selectedMetric]);
      })
      .style('stroke-width', d => Math.max(1, d.strength / 2))
      .style('opacity', 0.7)
      .style('stroke-linecap', 'round');

    // Create node groups
    const nodeGroups = svg.append('g')
      .selectAll('g')
      .data(data.nodes)
      .enter()
      .append('g')
      .call(d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended));

    // Add circles for nodes
    const _circles = nodeGroups.append('circle')
      .attr('r', d => 8 + d.totalConnections * 1.5)
      .style('fill', d => getAdvancedNodeColor(d))
      .style('stroke', '#fff')
      .style('stroke-width', 2)
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        setSelectedNode(selectedNode?.id === d.id ? null : d);
        highlightConnections(d, data.links);
        if (onNodeClick) {
          onNodeClick(d.name);
        }
      })
      .on('mouseover', (event, d) => {
        showAdvancedTooltip(event, d, data.links);
      })
      .on('mouseout', () => setTooltip({ show: false, content: '', x: 0, y: 0 }));

    // Add labels
    const _labels = nodeGroups.append('text')
      .text(d => d.name.split(' ')[0])
      .attr('text-anchor', 'middle')
      .attr('dy', '0.3em')
      .style('font-size', '10px')
      .style('font-weight', 'bold')
      .style('fill', '#333')
      .style('pointer-events', 'none');

    // Add secondary labels for important nodes
    nodeGroups.filter(d => d.totalConnections > 5)
      .append('text')
      .text(d => d.name.split(' ')[1] || '')
      .attr('text-anchor', 'middle')
      .attr('dy', '1.5em')
      .style('font-size', '8px')
      .style('fill', '#666')
      .style('pointer-events', 'none');

    // Update positions on tick
    simulation.on('tick', () => {
      links
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      nodeGroups
        .attr('transform', d => `translate(${d.x}, ${d.y})`);
    });

    // Drag functions
    function dragstarted(event, d) {
      if (!event.active) simulation.alphaTarget(0.1).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event, d) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    // Highlight connections for selected node
    function highlightConnections(selectedNode, links) {
      const connectedLinks = links.filter(l =>
        l.source.id === selectedNode.id || l.target.id === selectedNode.id
      );

      // Reset all styles
      svg.selectAll('line').style('opacity', 0.1).style('stroke-width', 1);
      svg.selectAll('circle').style('opacity', 0.3);

      // Highlight connected links
      svg.selectAll('line')
        .filter(d => connectedLinks.includes(d))
        .style('opacity', 1)
        .style('stroke-width', d => Math.max(2, d.strength / 1.5));

      // Highlight connected nodes
      const connectedNodeIds = new Set();
      connectedLinks.forEach(l => {
        connectedNodeIds.add(l.source.id);
        connectedNodeIds.add(l.target.id);
      });

      svg.selectAll('circle')
        .filter(d => connectedNodeIds.has(d.id))
        .style('opacity', 1);
    }
  };

  // Arc Diagram - Linear arrangement with curved connections
  const renderArcDiagram = (svg, data) => {
    svg.selectAll('*').remove();

    // Sort nodes by group and then by connection count
    const sortedNodes = [...data.nodes].sort((a, b) => {
      if (a.group !== b.group) return a.group.localeCompare(b.group);
      return b.totalConnections - a.totalConnections;
    });

    const xScale = d3.scalePoint()
      .domain(sortedNodes.map(d => d.id))
      .range([100, width - 100])
      .padding(0.1);

    const yPosition = height / 2;

    // Draw arcs for relationships
    const _arcGenerator = d3.arc();

    data.links.forEach(link => {
      const sourceX = xScale(link.source);
      const targetX = xScale(link.target);
      const distance = Math.abs(targetX - sourceX);
      const radius = distance / 2;
      const midX = (sourceX + targetX) / 2;

      // Create curved path
      const pathData = `M ${sourceX} ${yPosition} Q ${midX} ${yPosition - radius * 0.8} ${targetX} ${yPosition}`;

      svg.append('path')
        .attr('d', pathData)
        .style('fill', 'none')
        .style('stroke', d3.scaleSequential(d3.interpolateRdYlGn).domain([1, 10])(link[selectedMetric]))
        .style('stroke-width', Math.max(1, link.strength / 3))
        .style('opacity', 0.6);
    });

    // Draw nodes
    svg.selectAll('.node')
      .data(sortedNodes)
      .enter()
      .append('circle')
      .attr('class', 'node')
      .attr('cx', d => xScale(d.id))
      .attr('cy', yPosition)
      .attr('r', d => 6 + d.totalConnections * 1.5)
      .style('fill', d => getAdvancedNodeColor(d))
      .style('stroke', '#fff')
      .style('stroke-width', 2)
      .on('mouseover', (event, d) => showAdvancedTooltip(event, d, data.links))
      .on('mouseout', () => setTooltip({ show: false, content: '', x: 0, y: 0 }));

    // Add labels
    svg.selectAll('.label')
      .data(sortedNodes)
      .enter()
      .append('text')
      .attr('class', 'label')
      .attr('x', d => xScale(d.id))
      .attr('y', yPosition + 25)
      .attr('text-anchor', 'middle')
      .style('font-size', '10px')
      .style('fill', '#333')
      .text(d => d.name.split(' ')[0])
      .attr('transform', d => `rotate(45, ${xScale(d.id)}, ${yPosition + 25})`);

    // Add group separators
    const _groups = [...new Set(sortedNodes.map(d => d.group))];
    let currentGroup = null;

    sortedNodes.forEach((node, _i) => {
      if (node.group !== currentGroup) {
        currentGroup = node.group;
        svg.append('line')
          .attr('x1', xScale(node.id) - 30)
          .attr('y1', yPosition - 50)
          .attr('x2', xScale(node.id) - 30)
          .attr('y2', yPosition + 50)
          .style('stroke', '#ccc')
          .style('stroke-dasharray', '2,2');

        svg.append('text')
          .attr('x', xScale(node.id) - 25)
          .attr('y', yPosition - 60)
          .style('font-size', '12px')
          .style('font-weight', 'bold')
          .style('fill', '#666')
          .text(currentGroup.toUpperCase());
      }
    });
  };

  // Chord Diagram - Show relationship flows
  const renderChordDiagram = (svg, data) => {
    svg.selectAll('*').remove();

    const radius = Math.min(width, height) * 0.4;
    const outerRadius = radius;
    const innerRadius = radius - 30;

    // Process data for chord diagram
    const nodeIndex = new Map();
    data.nodes.forEach((node, i) => nodeIndex.set(node.id, i));

    const matrix = Array(data.nodes.length).fill().map(() => Array(data.nodes.length).fill(0));

    data.links.forEach(link => {
      const sourceIndex = nodeIndex.get(link.source);
      const targetIndex = nodeIndex.get(link.target);
      if (sourceIndex !== undefined && targetIndex !== undefined) {
        matrix[sourceIndex][targetIndex] = link[selectedMetric];
        matrix[targetIndex][sourceIndex] = link[selectedMetric];
      }
    });

    const chord = d3.chord()
      .padAngle(0.05)
      .sortSubgroups(d3.descending);

    const arc = d3.arc()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius);

    const ribbon = d3.ribbon()
      .radius(innerRadius);

    const chords = chord(matrix);
    const color = d3.scaleOrdinal(d3.schemeCategory10);

    const g = svg.append('g')
      .attr('transform', `translate(${width / 2}, ${height / 2})`);

    // Draw groups (nodes)
    const group = g.append('g')
      .selectAll('g')
      .data(chords.groups)
      .enter()
      .append('g');

    group.append('path')
      .style('fill', (d, i) => color(data.nodes[i].group))
      .style('stroke', '#fff')
      .attr('d', arc)
      .on('mouseover', (event, d) => {
        const node = data.nodes[d.index];
        showAdvancedTooltip(event, node, data.links);
      })
      .on('mouseout', () => setTooltip({ show: false, content: '', x: 0, y: 0 }));

    group.append('text')
      .each(d => { d.angle = (d.startAngle + d.endAngle) / 2; })
      .attr('dy', '.35em')
      .attr('transform', d => `
        rotate(${(d.angle * 180 / Math.PI - 90)})
        translate(${outerRadius + 10})
        ${d.angle > Math.PI ? 'rotate(180)' : ''}
      `)
      .style('text-anchor', d => d.angle > Math.PI ? 'end' : null)
      .style('font-size', '10px')
      .text((d, i) => data.nodes[i].name.split(' ')[0]);

    // Draw ribbons (relationships)
    g.append('g')
      .selectAll('path')
      .data(chords)
      .enter()
      .append('path')
      .attr('d', ribbon)
      .style('fill', d => color(data.nodes[d.source.index].group))
      .style('opacity', 0.7)
      .style('stroke', '#fff')
      .style('stroke-width', 0.5)
      .on('mouseover', (event, d) => {
        const sourceNode = data.nodes[d.source.index];
        const targetNode = data.nodes[d.target.index];
        const link = data.links.find(l =>
          (l.source === sourceNode.id && l.target === targetNode.id) ||
          (l.source === targetNode.id && l.target === sourceNode.id)
        );
        if (link) {
          setTooltip({
            show: true,
            content: `${sourceNode.name} ↔ ${targetNode.name}\n${selectedMetric}: ${link[selectedMetric]}\nType: ${link.type}`,
            x: event.clientX + 10,
            y: event.clientY - 10
          });
        }
      })
      .on('mouseout', () => setTooltip({ show: false, content: '', x: 0, y: 0 }));
  };

  const getAdvancedNodeColor = (node) => {
    const groupColors = {
      leadership: '#1e40af',
      engineering: '#047857',
      sales: '#b91c1c',
      product: '#6b21a8',
      board: '#c2410c',
      other: '#4b5563'
    };

    // Adjust opacity based on selected metric
    const baseColor = d3.color(groupColors[node.group] || groupColors.other);
    const intensity = Math.max(0.4, Math.min(1, node[`avg${selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)}`] / 10));
    baseColor.opacity = intensity;
    return baseColor.toString();
  };

  const showAdvancedTooltip = (event, node, links) => {
    const nodeLinks = links.filter(l => l.source === node.id || l.target === node.id);
    const avgMetrics = {
      trust: nodeLinks.reduce((sum, l) => sum + l.trust, 0) / nodeLinks.length || 0,
      collaboration: nodeLinks.reduce((sum, l) => sum + l.collaboration, 0) / nodeLinks.length || 0,
      communication: nodeLinks.reduce((sum, l) => sum + l.communication, 0) / nodeLinks.length || 0
    };

    setTooltip({
      show: true,
      content: `${node.name}\nGroup: ${node.group}\nConnections: ${node.totalConnections}\nAvg Trust: ${avgMetrics.trust.toFixed(1)}\nAvg Collaboration: ${avgMetrics.collaboration.toFixed(1)}\nAvg Communication: ${avgMetrics.communication.toFixed(1)}`,
      x: event.clientX + 10,
      y: event.clientY - 10
    });
  };

  const processNetworkData = () => {
    const nodes = new Map();
    const links = [];

    // Use provided relationships (no fallback to mock data)
    const relationshipsToProcess = filteredRelationships || [];

    relationshipsToProcess.forEach(rel => {
      // Add nodes
      ['entity1', 'entity2'].forEach(entity => {
        const name = rel[entity];
        if (!nodes.has(name)) {
          nodes.set(name, {
            id: name,
            name: name,
            group: getPersonGroup(name),
            totalConnections: 0,
            avgTrust: 0,
            avgCollaboration: 0,
            avgCommunication: 0
          });
        }
      });

      // Add links
      links.push({
        source: rel.entity1,
        target: rel.entity2,
        trust: rel.trust,
        communication: rel.communication,
        psychological_safety: rel.psychological_safety,
        collaboration: rel.collaboration,
        alignment: rel.alignment,
        type: rel.relationship_type,
        evolution: rel.evolution,
        strength: (rel.trust + rel.collaboration + rel.alignment) / 3
      });
    });

    const nodeArray = Array.from(nodes.values());

    // Calculate node metrics
    nodeArray.forEach(node => {
      const nodeLinks = links.filter(l => l.source === node.id || l.target === node.id);
      node.totalConnections = nodeLinks.length;
      node.avgTrust = nodeLinks.reduce((sum, l) => sum + l.trust, 0) / nodeLinks.length || 0;
      node.avgCollaboration = nodeLinks.reduce((sum, l) => sum + l.collaboration, 0) / nodeLinks.length || 0;
      node.avgCommunication = nodeLinks.reduce((sum, l) => sum + l.communication, 0) / nodeLinks.length || 0;
    });

    return { nodes: nodeArray, links };
  };

  const getPersonGroup = (name) => {
    const groupMapping = {
      'Emily Carter': 'leadership',
      'James Mitchell': 'leadership',
      'Maria Gonzalez': 'leadership',
      'Daniel Park': 'leadership',
      'Kevin Chen': 'engineering',
      'Amy Chen': 'engineering',
      'Marcus Johnson': 'engineering',
      'Chris Taylor': 'engineering',
      'Priya Patel': 'engineering',
      'Alex Turner': 'sales',
      'Mike Chang': 'sales',
      'Christina Walsh': 'sales',
      'Robert Martinez': 'sales',
      'Michael Foster': 'product',
      'Sophie Martinez': 'product',
      'Jessica Rodriguez': 'product',
      'Sarah Chen': 'board',
      'David Kumar': 'board',
      'Lisa Thompson': 'board'
    };

    return groupMapping[name] || 'other';
  };

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    const data = processNetworkData();

    // Choose visualization based on data size and complexity
    if (data.nodes.length > 30) {
      renderChordDiagram(svg, data);
    } else if (data.nodes.length > 15) {
      renderArcDiagram(svg, data);
    } else {
      renderStableForceGraph(svg, data);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMetric, width, height, filteredRelationships]);

  return (
    <div style={{ position: 'relative' }}>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{ border: '1px solid #e5e7eb', borderRadius: '8px', background: '#fafafa' }}
      />

      {tooltip.show && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            background: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '12px',
            borderRadius: '6px',
            fontSize: '12px',
            lineHeight: '1.4',
            pointerEvents: 'none',
            zIndex: 1000,
            whiteSpace: 'pre-line',
            maxWidth: '250px'
          }}
        >
          {tooltip.content}
        </div>
      )}

      {selectedNode && (
        <div
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            background: 'rgba(255, 255, 255, 0.95)',
            padding: '12px',
            borderRadius: '6px',
            border: '1px solid #ccc',
            maxWidth: '200px'
          }}
        >
          <h4 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>{selectedNode.name}</h4>
          <p style={{ margin: '4px 0', fontSize: '12px' }}>Group: {selectedNode.group}</p>
          <p style={{ margin: '4px 0', fontSize: '12px' }}>Connections: {selectedNode.totalConnections}</p>
          <p style={{ margin: '4px 0', fontSize: '12px' }}>Avg Trust: {selectedNode.avgTrust.toFixed(1)}</p>
          <button
            onClick={() => setSelectedNode(null)}
            style={{
              marginTop: '8px',
              padding: '4px 8px',
              fontSize: '12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              background: '#fff',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

export default AdvancedNetworkViz;